import React from 'react'
// libs
import { matchPath } from 'react-router'
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { getFormValues, InjectedFormProps, reduxForm } from 'redux-form'
// components
import { ReduxInput } from 'components/FormElements'
import { PopoverModal } from 'components/UI'
// views
import { ActiveFilterIconWrapper, ArrowDropDownIcon, Form, SearchRoundedIcon, SearchWrapper } from './views'
// constants
import { Forms } from 'constants/forms'
// types
import { IOption } from 'components/FormElements/Select'
import { WrappedFieldInputProps } from 'redux-form/lib/Field'
// themes
import { colors } from 'themes'
import { SearchIconWrapper } from 'themes/views/layouts'
// context
import { TableManagementContext } from 'containers/Search/layers/BL'
// utils
import { getValueFromOption } from 'utils/form'

const MODAL_PROPS = {
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'right' as const
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'right' as const
  }
}

interface Props {
  name?: string
  filter?: (props: any) => React.ReactNode
  onFilterOpen?: () => void
  isFilterActive?: boolean
}

type TFormValues = { [key: string]: any }

const Search: React.FunctionComponent<Props & InjectedFormProps<any, Props>> = ({
  name = 'description',
  reset,
  filter,
  onFilterOpen,
  isFilterActive,
  handleSubmit
}) => {
  const router = useRouter()

  const {
    handleSetFilter: onSelectChange,
    handleSetSearch: onInputChange,
    clearFilter: clearFilter
  } = React.useContext(TableManagementContext)

  const [isFocused, setFocus] = React.useState(false)

  const { formValues } = useSelector((state) => ({
    formValues: getFormValues(Forms.PAGE_SEARCH_FORM)(state)
  }))

  const getFilterValues = (formValues: TFormValues): TFormValues => {
    if (!formValues) return

    const variables = {}

    Object.keys(formValues).forEach((key) => {
      if (Array.isArray(formValues[key])) {
        variables[key] = formValues[key].map(({ value }) => value)
      } else if (formValues[key].value) {
        variables[key] = formValues[key].value
      } else {
        variables[key] = formValues[key]
      }
    })

    return variables
  }

  const handleSelectChange = (option: IOption | IOption[], input: WrappedFieldInputProps) => {
    if (matchPath('/plans/:companyId/edit/:id', router.pathname)) {
      return
    }

    const variables = getFilterValues(formValues)

    const filter = {
      ...variables,
      [input.name]: Array.isArray(option) ? option.map(({ value }) => value) : getValueFromOption(option)
    }

    onSelectChange(filter)
  }

  const handleInputFocus = () => setFocus(true)

  const handleInputBlur = () => setFocus(false)

  const handleClearFilter = () => {
    onInputChange('')
    clearFilter()
    reset()
  }

  return (
    <Form onSubmit={handleSubmit} $isFocused={isFocused}>
      <SearchWrapper>
        <SearchIconWrapper>
          <SearchRoundedIcon htmlColor={colors.white} />
        </SearchIconWrapper>
        <ReduxInput
          name={name}
          withEllipsis
          disableUnderline
          inputColor={colors.white}
          placeholder={'Search...'}
          handleOnBlur={handleInputBlur}
          handleOnFocus={handleInputFocus}
          handleInputChange={onInputChange as any}
        />

        {filter && (
          <PopoverModal
            {...MODAL_PROPS}
            width={'450px'}
            trigger={
              <ActiveFilterIconWrapper $isActive={false}>
                <ArrowDropDownIcon htmlColor={colors.white} />
              </ActiveFilterIconWrapper>
            }>
            {({ closePopup }) => filter({ clearFilter: handleClearFilter, closePopup, handleSelectChange, reset })}
          </PopoverModal>
        )}
        {onFilterOpen && (
          <ActiveFilterIconWrapper $isActive={!!isFilterActive} onClick={onFilterOpen}>
            <ArrowDropDownIcon htmlColor={colors.white} />
          </ActiveFilterIconWrapper>
        )}
      </SearchWrapper>
    </Form>
  )
}

export default reduxForm<any, Props>({
  form: Forms.PAGE_SEARCH_FORM,
  destroyOnUnmount: false
})(Search)
